/**
 * Connector identity — what a key CONNECTS TO drives its icon, so a rack of many keys is scannable by shape: GitHub
 * keys wear the GitHub mark (both App-OAuth `github` and `pat` with a github provider), database connections a
 * database, SSH a terminal, and so on. Shared by the Keybook page and the app settings' key picker. The app settings
 * know a key's credential type but not its provider, so there a GitHub personal access token shows the key icon, not
 * the GitHub mark.
 *
 * Nothing here is read when the module loads — only when an icon is asked for: suites that mock `@papan/core-ui/ui`
 * without `GithubIcon` (they render screens that import this file) would otherwise fail at import.
 */
import type { ComponentType } from 'react'
import { Database, Terminal, FileJson, ShieldCheck, KeyRound, Key, Wrench } from 'lucide-react'
import { GithubIcon } from '@papan/core-ui/ui'

export type ConnectorIcon = ComponentType<{ size?: number; className?: string }>

export function connectorIcon(entry: { credentialType: string; provider?: string | null }): ConnectorIcon {
  if (entry.provider === 'github') return GithubIcon
  // A key's type is free text from whoever made it: a switch over the known ones, so `constructor` / `__proto__`
  // never pick up Object's prototype (rendering that crashed the page).
  switch (entry.credentialType) {
    case 'github': return GithubIcon
    case 'connection_string': return Database
    case 'ssh_key': return Terminal
    case 'service_account': return FileJson
    case 'certificate': return ShieldCheck
    case 'api_key':
    case 'bearer':
    case 'pat': return KeyRound
    case 'password': return Key
    case 'custom': return Wrench
    default: return Key
  }
}
